import * as React from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { Button, type ButtonProps } from '@/client/components/ui/button';

export interface ResetFiltersButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  /** The route path for sessionStorage key (e.g., '/encoders') */
  routePath: string;
  /** Default values to compare against - filters are "active" if they differ from these */
  defaultValues: Record<string, unknown>;
}

/**
 * A button that resets URL search params and clears sessionStorage for a route.
 * Only renders when there are active (non-default) filters.
 */
export const ResetFiltersButton = React.forwardRef<HTMLButtonElement, ResetFiltersButtonProps>(
  ({ routePath, defaultValues, variant = 'ghost', size = 'icon', className, ...props }, ref) => {
    const navigate = useNavigate();
    const router = useRouter();
    const search = router.state.location.search as Record<string, unknown>;

    // Check if any filters are active (differ from defaults)
    const hasActiveFilters = Object.entries(defaultValues).some(([key, defaultValue]) => {
      const currentValue = search[key];
      // Filter is active if current value exists and differs from default
      if (currentValue === undefined || currentValue === '') {
        return false;
      }
      return currentValue !== defaultValue;
    });

    // Also check for any search params not in defaultValues (like clientID passed from other routes)
    const hasExtraParams = Object.keys(search).some(
      (key) => !(key in defaultValues) && search[key] !== undefined && search[key] !== ''
    );

    const isDisabled = !hasActiveFilters && !hasExtraParams;

    const handleReset = () => {
      // Clear sessionStorage for this route
      sessionStorage.removeItem(`search:${routePath}`);
      // Navigate to route with empty search params
      navigate({ to: routePath, search: {} });
    };

    // Use outline variant when filters are active to make it more prominent
    const activeVariant = isDisabled ? variant : 'outline';

    return (
      <Button
        ref={ref}
        variant={activeVariant}
        size={size}
        onClick={handleReset}
        title="Reset filters"
        className={className}
        disabled={isDisabled}
        {...props}
      >
        <X className="h-4 w-4" />
      </Button>
    );
  }
);

ResetFiltersButton.displayName = 'ResetFiltersButton';
